import React, { useState } from 'react'
import styled from 'styled-components'
import { Link, useNavigate } from 'react-router-dom'
import { API_ORIGIN } from '../config'
import { CallToAction, Container, StoreCard } from '../components/Item'
import { useAuth } from '../hooks/useAuth'

// ── Shared layout ──────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
    width: 100%;
    max-width: 460px;
    margin: 48px auto 0;
    padding: 0 16px;
    box-sizing: border-box;
    text-align: center;
`

const Heading = styled.h1`
    font-size: clamp(1.5rem, 3vw, 2.1rem);
    font-weight: 700;
    margin: 0 0 10px;
    color: ${(props) => props.theme.text};
    letter-spacing: 0.01em;
`

const Sub = styled.p`
    color: ${(props) => props.theme.secondaryText};
    margin: 0 0 28px;
    font-size: 0.95rem;
    line-height: 1.6;
`

const BackLink = styled(Link)`
    display: inline-block;
    margin-top: 22px;
    font-size: 0.82rem;
    color: ${(props) => props.theme.secondaryText};
    text-decoration: none;
    opacity: 0.6;
    transition: opacity 0.2s ease;
    &:hover {
        opacity: 1;
    }
`

const ErrorText = styled.p`
    color: #ff6b6b;
    font-size: 0.85rem;
    margin: 2px 0 0;
    text-align: left;
`

// ── Form ───────────────────────────────────────────────────────────────────────

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
`

const Input = styled.input`
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    padding: 14px 16px;
    font-size: 1rem;
    color: ${(props) => props.theme.text};
    outline: none;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.2s ease;
    &::placeholder {
        color: ${(props) => props.theme.secondaryText};
        opacity: 0.55;
    }
    &:focus {
        border-color: rgba(255, 255, 255, 0.38);
    }
`

const SubmitButton = styled.button<{ $loading?: boolean }>`
    padding: 14px 20px;
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.09);
    color: ${(props) => props.theme.text};
    border: 1px solid ${(props) => props.theme.border};
    border-radius: 12px;
    cursor: ${({ $loading }) => ($loading ? 'not-allowed' : 'pointer')};
    opacity: ${({ $loading }) => ($loading ? 0.55 : 1)};
    transition:
        background 0.2s ease,
        transform 0.2s ease;
    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.16);
        transform: translateY(-1px);
    }
`

// ── Tabs ───────────────────────────────────────────────────────────────────────

const Tabs = styled.div`
    display: flex;
    gap: 0;
    margin-bottom: 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`

const Tab = styled.button<{ $active: boolean }>`
    flex: 1;
    padding: 10px;
    background: none;
    border: none;
    border-bottom: 2px solid
        ${({ $active }) => ($active ? 'rgba(255,255,255,0.7)' : 'transparent')};
    color: ${({ $active, theme }) =>
        $active ? theme.text : theme.secondaryText};
    font-size: 0.9rem;
    font-weight: ${({ $active }) => ($active ? 600 : 400)};
    cursor: pointer;
    transition:
        color 0.2s ease,
        border-color 0.2s ease;
    margin-bottom: -1px;
`

// ── Auth gate ──────────────────────────────────────────────────────────────────

type AuthTab = 'register' | 'login'

const AuthGate: React.FC<{ onAuth: () => void }> = ({ onAuth }) => {
    const [tab, setTab] = useState<AuthTab>('register')
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const { register, loginWithCredentials } = useAuth()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError(null)
        try {
            if (tab === 'register') {
                await register(name, email, password)
            } else {
                await loginWithCredentials(email, password)
            }
            onAuth()
        } catch (err: any) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <Tabs>
                <Tab
                    $active={tab === 'register'}
                    onClick={() => {
                        setTab('register')
                        setError(null)
                    }}
                >
                    Create account
                </Tab>
                <Tab
                    $active={tab === 'login'}
                    onClick={() => {
                        setTab('login')
                        setError(null)
                    }}
                >
                    Sign in
                </Tab>
            </Tabs>

            <Form onSubmit={handleSubmit}>
                {tab === 'register' && (
                    <Input
                        type="text"
                        placeholder="Your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                    />
                )}
                <Input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                />
                <Input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                />
                {error && <ErrorText>{error}</ErrorText>}
                <SubmitButton
                    type="submit"
                    disabled={loading}
                    $loading={loading}
                >
                    {loading
                        ? tab === 'register'
                            ? 'Creating account…'
                            : 'Signing in…'
                        : tab === 'register'
                          ? 'Create account & get plugin'
                          : 'Sign in'}
                </SubmitButton>
            </Form>

            {tab === 'register' && (
                <Sub
                    style={{
                        fontSize: '0.78rem',
                        marginTop: '-8px',
                        marginBottom: 0,
                    }}
                >
                    By registering you'll receive the GSSNHR newsletter.
                    Unsubscribe any time.
                </Sub>
            )}
        </>
    )
}

// ── Logged-in download dashboard ───────────────────────────────────────────────

const WelcomeBar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 28px;
    color: ${(props) => props.theme.secondaryText};
    font-size: 0.85rem;
`

const LogoutBtn = styled.button`
    background: none;
    border: none;
    color: ${(props) => props.theme.secondaryText};
    cursor: pointer;
    font-size: 0.82rem;
    opacity: 0.6;
    padding: 0;
    transition: opacity 0.2s ease;
    &:hover {
        opacity: 1;
    }
`

const PluginList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
`

interface PluginInfo {
    name: string
    platform: string
    filename: string
}

const PluginDashboard: React.FC = () => {
    const { user, token, logout } = useAuth()
    const navigate = useNavigate()
    const [downloading, setDownloading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [pluginInfo, setPluginInfo] = useState<PluginInfo | null>(null)

    React.useEffect(() => {
        fetch(`${API_ORIGIN}/plugins/info`)
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => data && setPluginInfo(data))
            .catch(() => {})
    }, [])

    const handleLogout = () => {
        logout()
        navigate('/')
    }

    const handleDownload = async () => {
        setDownloading(true)
        setError(null)
        try {
            const res = await fetch(`${API_ORIGIN}/plugins/request-download`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            })
            const data = await res.json()
            if (!res.ok)
                throw new Error(data.error || 'Could not start download.')
            // Navigate directly — browser handles the file download
            window.location.href = `${API_ORIGIN}/plugins/download?t=${data.downloadToken}`
        } catch (err: any) {
            setError(err.message)
        } finally {
            setDownloading(false)
        }
    }

    return (
        <>
            <WelcomeBar>
                <span>
                    Hey, <strong>{user?.name}</strong> 👋
                </span>
                <LogoutBtn onClick={handleLogout}>Sign out</LogoutBtn>
            </WelcomeBar>

            <PluginList>
                <StoreCard as="div" style={{ cursor: 'default' }}>
                    <div style={{ textAlign: 'left' }}>
                        <div
                            style={{
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                marginBottom: 2,
                            }}
                        >
                            {pluginInfo ? pluginInfo.name : '…'}
                        </div>
                        <div style={{ fontSize: '0.8rem', opacity: 0.55 }}>
                            {pluginInfo
                                ? `Wash effect — ${pluginInfo.platform}`
                                : ''}
                        </div>
                    </div>
                    <CallToAction
                        as="button"
                        onClick={handleDownload}
                        style={{
                            cursor: downloading ? 'not-allowed' : 'pointer',
                            opacity: downloading ? 0.5 : 1,
                            border: 'none',
                        }}
                    >
                        {downloading ? '…' : 'Download'}
                    </CallToAction>
                </StoreCard>
            </PluginList>

            {error && <ErrorText style={{ marginTop: 12 }}>{error}</ErrorText>}
        </>
    )
}

// ── Page ───────────────────────────────────────────────────────────────────────

const VstDownload: React.FC = () => {
    const { isAuthenticated } = useAuth()
    const [authed, setAuthed] = useState(isAuthenticated)

    return (
        <Container>
            <PageWrapper>
                <Heading>Plugins</Heading>
                {!authed ? (
                    <>
                        <Sub>
                            Create a free account to download{' '}
                            <strong>WetCat</strong> and get notified when new
                            plugins drop.
                        </Sub>
                        <AuthGate onAuth={() => setAuthed(true)} />
                    </>
                ) : (
                    <PluginDashboard />
                )}
                <BackLink to="/">← Back to releases</BackLink>
            </PageWrapper>
        </Container>
    )
}

export default VstDownload
